// app/components/Plugins/Automations/nodes/implementations/IndicatorNodeImpl.ts

import { BaseNode } from '../../core/BaseNode';
import { ExecutionContext } from '../../core/protocols';
import { AutomationNode, IndicatorNodeData } from '../../types';
import { RSI, MACD, SMA, EMA, BollingerBands } from 'technicalindicators';

/**
 * Implementation of the Indicator Node protocol
 * Responsible for evaluating technical indicators and market conditions
 */
export class IndicatorNodeImpl extends BaseNode {
  constructor(node: AutomationNode) {
    super(node);
  }

  /**
   * Process the indicator node
   * Evaluates the specified indicator against market data and conditions
   */
  async process(context: ExecutionContext): Promise<boolean> {
    // Get indicator configuration from node data
    const data = this.data as IndicatorNodeData;
    const { indicator, timeframe, condition } = data;
    
    if (!indicator || !timeframe || !condition) {
      context.addLog('Error: Incomplete indicator configuration');
      return false;
    }
    
    // Log the indicator evaluation
    context.addLog(`Evaluating ${indicator} on ${timeframe} timeframe for ${context.ticker}`);
    
    try {
      // Get market data from the data provider
      if (!context.dataProvider) {
        context.addLog('Error: Data provider not available');
        return false;
      }
      
      const marketData = await context.dataProvider.getMarketData(context.ticker, timeframe);
      if (!marketData || !marketData.close || marketData.close.length === 0) {
        context.addLog(`Error: No market data available for ${context.ticker}`);
        return false;
      }
      
      // Calculate the indicator value based on the indicator type
      let indicatorValue: number | null = null;
      let thresholdValue: number | string | null = null;
      
      switch (indicator) {
        case 'Price': {
          // For Price indicator, we directly use the price value as the indicator value
          const priceType = data.priceType || 'close';
          
          // Get the appropriate price data based on the selected price type
          const priceData = marketData[priceType] as number[];
          if (!priceData || priceData.length === 0) {
            context.addLog(`Error: No ${priceType} price data available for ${context.ticker}`);
            return false;
          }
          
          // Use the latest price value as the indicator value
          indicatorValue = priceData[priceData.length - 1];
          thresholdValue = parseFloat(data.value);
          
          context.addLog(`Current ${priceType} price: ${indicatorValue !== null ? indicatorValue.toFixed(2) : 'N/A'}`);
          context.addLog(`Threshold value: ${thresholdValue}`);
          
          // Store previous price value for crossing conditions
          if (priceData.length >= 2) {
            context.data.prevIndicatorValue = priceData[priceData.length - 2];
          }
          break;
        }
        
        case 'RSI': {
          const period = data.rsiPeriod || 14;
          const rsiValues = RSI.calculate({
            values: marketData.close,
            period: period
          });
          
          if (rsiValues.length === 0) {
            context.addLog(`Error: Failed to calculate RSI for ${context.ticker}`);
            return false;
          }
          
          indicatorValue = rsiValues[rsiValues.length - 1];
          thresholdValue = parseFloat(data.value);
          
          context.addLog(`RSI(${period}) value: ${indicatorValue.toFixed(2)}`);
          break;
        }
        
        case 'MACD': {
          const fastPeriod = data.macdFastPeriod || 12;
          const slowPeriod = data.macdSlowPeriod || 26;
          const signalPeriod = data.macdSignalPeriod || 9;
          
          const macdResults = MACD.calculate({
            values: marketData.close,
            fastPeriod,
            slowPeriod,
            signalPeriod,
            SimpleMAOscillator: false,
            SimpleMASignal: false
          });
          
          if (macdResults.length === 0) {
            context.addLog(`Error: Failed to calculate MACD for ${context.ticker}`);
            return false;
          }
          
          const latestMACD = macdResults[macdResults.length - 1];
          
          // MACD has multiple values we can check
          indicatorValue = latestMACD.MACD || 0;
          const signalValue = latestMACD.signal || 0;
          const histogramValue = latestMACD.histogram || 0;
          
          // Use the user-entered value as the threshold
          thresholdValue = parseFloat(data.value);
          
          context.addLog(`MACD(${fastPeriod},${slowPeriod},${signalPeriod}) values:`);
          context.addLog(`- MACD Line: ${indicatorValue.toFixed(2)}`);
          context.addLog(`- Signal Line: ${signalValue.toFixed(2)}`);
          context.addLog(`- Histogram: ${histogramValue.toFixed(2)}`);
          context.addLog(`- Threshold Value: ${thresholdValue}`);
          
          // Store additional MACD values in context for other nodes
          context.data.macdSignal = signalValue;
          context.data.macdHistogram = histogramValue;
          
          // Store previous MACD value for crossing conditions
          if (macdResults.length >= 2) {
            const prevMACD = macdResults[macdResults.length - 2];
            context.data.prevIndicatorValue = prevMACD.MACD || 0;
          }
          break;
        }
        
        case 'SMA': {
          const period = data.smaPeriod || 20;
          const smaValues = SMA.calculate({
            values: marketData.close,
            period: period
          });
          
          if (smaValues.length === 0) {
            context.addLog(`Error: Failed to calculate SMA for ${context.ticker}`);
            return false;
          }
          
          indicatorValue = smaValues[smaValues.length - 1];
          thresholdValue = parseFloat(data.value);
          
          context.addLog(`SMA(${period}) value: ${indicatorValue.toFixed(2)}`);
          break;
        }
        
        case 'EMA': {
          const period = data.emaPeriod || 20;
          const emaValues = EMA.calculate({
            values: marketData.close,
            period: period
          });
          
          if (emaValues.length === 0) {
            context.addLog(`Error: Failed to calculate EMA for ${context.ticker}`);
            return false;
          }
          
          indicatorValue = emaValues[emaValues.length - 1];
          thresholdValue = parseFloat(data.value);
          
          context.addLog(`EMA(${period}) value: ${indicatorValue.toFixed(2)}`);
          break;
        }
        
        case 'Bollinger Bands': {
          const period = data.bbPeriod || 20;
          const stdDev = data.bbStdDev || 2;
          
          const bbResults = BollingerBands.calculate({
            values: marketData.close,
            period: period,
            stdDev: stdDev
          });
          
          if (bbResults.length === 0) {
            context.addLog(`Error: Failed to calculate Bollinger Bands for ${context.ticker}`);
            return false;
          }
          
          const latestBB = bbResults[bbResults.length - 1];
          const selectedBand = data.value || 'upper';
          
          // Get the appropriate band value based on user selection
          switch (selectedBand) {
            case 'upper':
              indicatorValue = latestBB.upper;
              break;
            case 'middle':
              indicatorValue = latestBB.middle;
              break;
            case 'lower':
              indicatorValue = latestBB.lower;
              break;
            default:
              indicatorValue = latestBB.upper;
          }
          
          thresholdValue = selectedBand;
          
          context.addLog(`Bollinger Bands(${period},${stdDev}) values:`);
          context.addLog(`- Upper Band: ${latestBB.upper.toFixed(2)}`);
          context.addLog(`- Middle Band: ${latestBB.middle.toFixed(2)}`);
          context.addLog(`- Lower Band: ${latestBB.lower.toFixed(2)}`);
          
          // Store all BB values in context for other nodes
          context.data.bbUpper = latestBB.upper;
          context.data.bbMiddle = latestBB.middle;
          context.data.bbLower = latestBB.lower;
          break;
        }
        
        default:
          context.addLog(`Error: Unknown indicator ${indicator}`);
          return false;
      }
      
      // Get the latest price for comparison
      const latestPrice = marketData.close[marketData.close.length - 1];
      context.data.latestPrice = latestPrice;
      
      // Evaluate the condition
      let conditionMet = false;
      
      // For Bollinger Bands, we compare the price to the selected band
      if (indicator === 'Bollinger Bands') {
        switch (condition) {
          case 'crosses_above':
            // Check if price crossed above the selected band
            if (marketData.close.length >= 2) {
              const prevPrice = marketData.close[marketData.close.length - 2];
              const bandKey = typeof thresholdValue === 'string' ? 
                `bb${thresholdValue.charAt(0).toUpperCase()}${thresholdValue.slice(1)}` : 
                'bbUpper';
              const prevBBValue = context.data[bandKey];
              conditionMet = prevPrice <= prevBBValue && latestPrice > (indicatorValue || 0);
            }
            break;
          case 'crosses_below':
            // Check if price crossed below the selected band
            if (marketData.close.length >= 2) {
              const prevPrice = marketData.close[marketData.close.length - 2];
              const bandKey = typeof thresholdValue === 'string' ? 
                `bb${thresholdValue.charAt(0).toUpperCase()}${thresholdValue.slice(1)}` : 
                'bbLower';
              const prevBBValue = context.data[bandKey];
              conditionMet = prevPrice >= prevBBValue && latestPrice < (indicatorValue || 0);
            }
            break;
          case 'is_above':
            conditionMet = latestPrice > (indicatorValue || 0);
            break;
          case 'is_below':
            conditionMet = latestPrice < (indicatorValue || 0);
            break;
          default:
            context.addLog(`Error: Unsupported condition ${condition} for Bollinger Bands`);
            return false;
        }
      } else {
        // For other indicators, we compare the indicator value to the threshold
        switch (condition) {
          case 'crosses_above':
            // We need at least two data points to check for crossing
            if (marketData.close.length >= 2) {
              // Get previous indicator value
              const prevIndicatorValue = context.data.prevIndicatorValue;
              if (prevIndicatorValue !== undefined) {
                const numThreshold = typeof thresholdValue === 'string' ? parseFloat(thresholdValue) : (thresholdValue || 0);
                conditionMet = prevIndicatorValue <= numThreshold && (indicatorValue || 0) > numThreshold;
                
                // For MACD, log the specific comparison being made
                if (indicator === 'MACD') {
                  context.addLog(`MACD crossing above: Previous=${prevIndicatorValue.toFixed(2)}, Current=${indicatorValue?.toFixed(2)}, Threshold=${numThreshold}`);
                }
              }
            }
            break;
          case 'crosses_below':
            // We need at least two data points to check for crossing
            if (marketData.close.length >= 2) {
              // Get previous indicator value
              const prevIndicatorValue = context.data.prevIndicatorValue;
              if (prevIndicatorValue !== undefined) {
                const numThreshold = typeof thresholdValue === 'string' ? parseFloat(thresholdValue) : (thresholdValue || 0);
                conditionMet = prevIndicatorValue >= numThreshold && (indicatorValue || 0) < numThreshold;
                
                // For MACD, log the specific comparison being made
                if (indicator === 'MACD') {
                  context.addLog(`MACD crossing below: Previous=${prevIndicatorValue.toFixed(2)}, Current=${indicatorValue?.toFixed(2)}, Threshold=${numThreshold}`);
                }
              }
            }
            break;
          case 'is_above':
            const aboveThreshold = typeof thresholdValue === 'string' ? parseFloat(thresholdValue) : (thresholdValue || 0);
            conditionMet = (indicatorValue || 0) > aboveThreshold;
            
            // For MACD, log the specific comparison being made
            if (indicator === 'MACD') {
              context.addLog(`MACD is above: Current=${indicatorValue?.toFixed(2)}, Threshold=${aboveThreshold}`);
            }
            break;
          case 'is_below':
            const belowThreshold = typeof thresholdValue === 'string' ? parseFloat(thresholdValue) : (thresholdValue || 0);
            conditionMet = (indicatorValue || 0) < belowThreshold;
            
            // For MACD, log the specific comparison being made
            if (indicator === 'MACD') {
              context.addLog(`MACD is below: Current=${indicatorValue?.toFixed(2)}, Threshold=${belowThreshold}`);
            }
            break;
          case 'is_increasing':
            // Check if the indicator is increasing
            if (marketData.close.length >= 2) {
              const prevIndicatorValue = context.data.prevIndicatorValue;
              if (prevIndicatorValue !== undefined) {
                conditionMet = (indicatorValue || 0) > prevIndicatorValue;
                
                // For MACD, log the specific comparison being made
                if (indicator === 'MACD') {
                  context.addLog(`MACD is increasing: Previous=${prevIndicatorValue.toFixed(2)}, Current=${indicatorValue?.toFixed(2)}`);
                  // Note: For 'is_increasing', we're comparing current to previous value, not to threshold
                }
              }
            }
            break;
          case 'is_decreasing':
            // Check if the indicator is decreasing
            if (marketData.close.length >= 2) {
              const prevIndicatorValue = context.data.prevIndicatorValue;
              if (prevIndicatorValue !== undefined) {
                conditionMet = (indicatorValue || 0) < prevIndicatorValue;
                
                // For MACD, log the specific comparison being made
                if (indicator === 'MACD') {
                  context.addLog(`MACD is decreasing: Previous=${prevIndicatorValue.toFixed(2)}, Current=${indicatorValue?.toFixed(2)}`);
                  // Note: For 'is_decreasing', we're comparing current to previous value, not to threshold
                }
              }
            }
            break;
          default:
            context.addLog(`Error: Unknown condition ${condition}`);
            return false;
        }
      }
      
      // Store the current indicator value for next evaluation
      context.data.prevIndicatorValue = indicatorValue;
      
      // Store the result in context data for other nodes to use
      context.data.indicatorValue = indicatorValue;
      context.data.conditionMet = conditionMet;
      
      // Log the result
      if (indicator === 'Bollinger Bands') {
        context.addLog(`Condition: Price ${condition.replace('_', ' ')} ${thresholdValue} band`);
      } else {
        context.addLog(`Condition: ${indicator} ${condition.replace('_', ' ')} ${thresholdValue}`);
      }
      context.addLog(`Condition is ${conditionMet ? 'MET' : 'NOT MET'}`);
      
      // Return true if the condition is met, false otherwise
      return conditionMet;
      
    } catch (error) {
      context.addLog(`Error evaluating indicator: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  /**
   * Validate the indicator node configuration
   */
  validate() {
    const result = super.validate();
    const errors = [...result.errors];
    
    // Check if required fields are specified
    const data = this.data as IndicatorNodeData;
    if (!data.indicator) {
      errors.push('No indicator specified');
    }
    if (!data.timeframe) {
      errors.push('No timeframe specified');
    }
    if (!data.condition) {
      errors.push('No condition specified');
    }
    if (!data.value && data.indicator !== 'Bollinger Bands') {
      errors.push('No threshold value specified');
    }
    
    // Validate indicator-specific parameters
    switch (data.indicator) {
      case 'Price':
        if (!data.priceType) {
          errors.push('No price type specified');
        }
        break;
      case 'RSI':
        if (!data.rsiPeriod || data.rsiPeriod < 1) {
          errors.push('Invalid RSI period');
        }
        break;
      case 'MACD':
        if (!data.macdFastPeriod || data.macdFastPeriod < 1) {
          errors.push('Invalid MACD fast period');
        }
        if (!data.macdSlowPeriod || data.macdSlowPeriod < 1) {
          errors.push('Invalid MACD slow period');
        }
        if (!data.macdSignalPeriod || data.macdSignalPeriod < 1) {
          errors.push('Invalid MACD signal period');
        }
        break;
      case 'SMA':
        if (!data.smaPeriod || data.smaPeriod < 1) {
          errors.push('Invalid SMA period');
        }
        break;
      case 'EMA':
        if (!data.emaPeriod || data.emaPeriod < 1) {
          errors.push('Invalid EMA period');
        }
        break;
      case 'Bollinger Bands':
        if (!data.bbPeriod || data.bbPeriod < 1) {
          errors.push('Invalid Bollinger Bands period');
        }
        if (!data.bbStdDev || data.bbStdDev <= 0) {
          errors.push('Invalid Bollinger Bands standard deviation');
        }
        break;
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get the input ports for this node
   * Indicator node has one input
   */
  getInputs(): string[] {
    return ['in'];
  }

  /**
   * Get the output ports for this node
   * Indicator node has two outputs: true and false
   */
  getOutputs(): string[] {
    return ['true', 'false'];
  }
}